import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Camera, Upload } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InwardPage = ({ user, token }) => {
  const [hubs, setHubs] = useState([]);
  const [selectedHub, setSelectedHub] = useState(user?.hub_id || "");
  const [vehicleNumbers, setVehicleNumbers] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [useDropdownVehicle, setUseDropdownVehicle] = useState(true);
  const [formData, setFormData] = useState({
    load_weight: "",
    vehicle_number: "",
    material_type: "",
    photo_base64: ""
  });
  const [inwardList, setInwardList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    fetchHubs();
    fetchInwardList();
    fetchVehicleNumbers();
    fetchMaterialTypes();
  }, []);

  const fetchVehicleNumbers = async () => {
    try {
      const response = await axios.get(`${API}/vehicle-numbers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicleNumbers(response.data);
    } catch (error) {
      console.error("Error fetching vehicle numbers:", error);
    }
  };

  const fetchMaterialTypes = async () => {
    try {
      const response = await axios.get(`${API}/material-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMaterialTypes(response.data);
    } catch (error) {
      console.error("Error fetching material types:", error);
    }
  };

  const fetchHubs = async () => {
    try {
      const response = await axios.get(`${API}/hubs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHubs(response.data);
      if (response.data.length > 0 && !user?.hub_id) {
        setSelectedHub(response.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching hubs:", error);
    }
  };

  const fetchInwardList = async () => {
    try {
      const response = await axios.get(`${API}/inward`, {
        headers: { Authorization: `Bearer ${token}` },
        params: user?.hub_id ? { hub_id: user.hub_id } : {}
      });
      setInwardList(response.data);
    } catch (error) {
      console.error("Error fetching inward list:", error);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, photo_base64: reader.result });
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API}/inward`,
        {
          hub_id: selectedHub,
          load_weight: parseFloat(formData.load_weight),
          vehicle_number: formData.vehicle_number,
          material_type: formData.material_type,
          photo_base64: formData.photo_base64
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Inward entry created successfully!");
      setFormData({ load_weight: "", vehicle_number: "", material_type: "", photo_base64: "" });
      setPreviewImage(null);
      fetchInwardList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create inward entry");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="inward-page">
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-heading tracking-tight">Inward Entry</h1>
        <p className="text-muted-foreground text-sm sm:text-base mt-1">Record incoming waste loads</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
        <Card className="shadow-lg border-border/50">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg sm:text-xl lg:text-2xl font-heading">New Entry</CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            <form onSubmit={handleSubmit} className="space-y-4">
              {!user?.hub_id && (
                <div className="space-y-2">
                  <Label htmlFor="hub" className="text-sm">Hub</Label>
                  <Select value={selectedHub} onValueChange={setSelectedHub}>
                    <SelectTrigger data-testid="inward-hub-select">
                      <SelectValue placeholder="Select Hub" />
                    </SelectTrigger>
                    <SelectContent>
                      {hubs.map((hub) => (
                        <SelectItem key={hub.id} value={hub.id}>
                          {hub.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="load_weight" className="text-sm">Load Weight (kg)</Label>
                <Input
                  id="load_weight"
                  data-testid="inward-weight-input"
                  type="number"
                  step="0.01"
                  placeholder="Enter weight"
                  value={formData.load_weight}
                  onChange={(e) => setFormData({ ...formData, load_weight: e.target.value })}
                  className="h-9 sm:h-10"
                  required
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="vehicle_number" className="text-sm">Vehicle Number</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setUseDropdownVehicle(!useDropdownVehicle)}
                    className="text-xs h-6 px-2"
                  >
                    {useDropdownVehicle ? "Manual" : "Select"}
                  </Button>
                </div>
                {useDropdownVehicle && vehicleNumbers.length > 0 ? (
                  <Select
                    value={formData.vehicle_number}
                    onValueChange={(value) => setFormData({ ...formData, vehicle_number: value })}
                  >
                    <SelectTrigger data-testid="inward-vehicle-select">
                      <SelectValue placeholder="Select vehicle" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleNumbers.map((vehicle) => (
                        <SelectItem key={vehicle.id} value={vehicle.vehicle_number}>
                          {vehicle.vehicle_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="vehicle_number"
                    data-testid="inward-vehicle-input"
                    type="text"
                    placeholder="e.g., MH-01-AB-1234"
                    value={formData.vehicle_number}
                    onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value })}
                    className="h-9 sm:h-10"
                    required
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="material_type" className="text-sm">Material Type</Label>
                <Select
                  value={formData.material_type}
                  onValueChange={(value) => setFormData({ ...formData, material_type: value })}
                >
                  <SelectTrigger data-testid="inward-material-select">
                    <SelectValue placeholder="Select Material" />
                  </SelectTrigger>
                  <SelectContent>
                    {materialTypes.length > 0 ? (
                      materialTypes.map((material) => (
                        <SelectItem key={material.id} value={material.name}>
                          {material.name}
                        </SelectItem>
                      ))
                    ) : (
                      <>
                        <SelectItem value="Plastic">Plastic</SelectItem>
                        <SelectItem value="Paper">Paper</SelectItem>
                        <SelectItem value="Metal">Metal</SelectItem>
                        <SelectItem value="Glass">Glass</SelectItem>
                        <SelectItem value="Organic">Organic</SelectItem>
                        <SelectItem value="E-Waste">E-Waste</SelectItem>
                        <SelectItem value="Mixed">Mixed</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="photo" className="text-sm">Load Photo</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="photo"
                    data-testid="inward-photo-input"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('photo').click()}
                    className="gap-2 text-sm h-9"
                    data-testid="inward-upload-button"
                  >
                    <Camera className="w-4 h-4" />
                    {previewImage ? "Change" : "Upload"}
                  </Button>
                </div>
              </div>

              <Button
                data-testid="inward-submit-button"
                type="submit"
                className="w-full h-9 sm:h-10"
                disabled={loading}
              >
                {loading ? "Saving..." : "Save Entry"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-4 sm:space-y-6">
          {previewImage && (
            <Card className="shadow-lg border-border/50">
              <CardHeader className="p-3 sm:p-4">
                <CardTitle className="text-base sm:text-lg font-heading">Photo Preview</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0">
                <img
                  src={previewImage}
                  alt="Load preview"
                  className="w-full h-40 sm:h-52 lg:h-64 object-cover rounded-lg"
                />
              </CardContent>
            </Card>
          )}

          {!previewImage && (
            <Card className="shadow-lg border-border/50 h-48 sm:h-64 lg:h-80">
              <CardContent className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <Upload className="w-10 h-10 sm:w-14 sm:h-14 lg:w-16 lg:h-16 mb-2 sm:mb-4" />
                <p className="text-sm sm:text-base lg:text-lg">Upload a photo to preview</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Card className="shadow-lg border-border/50">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-lg sm:text-xl lg:text-2xl font-heading">Recent Entries</CardTitle>
        </CardHeader>
        <CardContent className="p-0 sm:p-4 lg:p-6 sm:pt-0">
          <div className="overflow-x-auto">
            <Table data-testid="inward-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs sm:text-sm">Date</TableHead>
                  <TableHead className="text-xs sm:text-sm">Time</TableHead>
                  <TableHead className="text-xs sm:text-sm">Vehicle</TableHead>
                  <TableHead className="text-xs sm:text-sm">Material</TableHead>
                  <TableHead className="text-xs sm:text-sm">Weight</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inwardList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground text-sm">
                      No entries yet
                    </TableCell>
                  </TableRow>
                ) : (
                  inwardList.slice(0, 10).map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-xs sm:text-sm">{entry.date}</TableCell>
                      <TableCell className="text-xs sm:text-sm">{entry.time}</TableCell>
                      <TableCell className="text-xs sm:text-sm">{entry.vehicle_number}</TableCell>
                      <TableCell className="text-xs sm:text-sm">{entry.material_type}</TableCell>
                      <TableCell className="text-xs sm:text-sm">{entry.load_weight}kg</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InwardPage;
